import { fetchApi } from "@/lib/api-helpers";
import { z } from "zod";

const API_VER = "v202506";

// --- Response schemas ---

const braintreeClientTokenResponseSchema = z.object({
  client_token: z.string(),
  paypal_enabled: z.boolean().default(true),
});

const braintreePaymentResponseSchema = z.object({
  transaction_id: z.string(),
  payment_type: z.string(),
});

const braintreePaymentMethodResponseSchema = z.object({
  status: z.string().optional(),
  success: z.boolean().optional(),
  payment_method: z
    .object({
      id: z.number(),
      type: z.string(),
      token: z.string(),
    })
    .optional(),
  meta: z
    .object({
      request_id: z.string().optional(),
      timestamp: z.string().optional(),
    })
    .optional(),
});

// --- API functions ---

/**
 * Fetch a Braintree client token for SDK initialization.
 * GET /api/v202506/carts/:cartToken/braintree/client_token?payment_account_id=:id
 */
export async function getBraintreeClientToken(
  cartToken: string,
  paymentAccountId: string,
) {
  return fetchApi({
    endpoint: `/${API_VER}/carts/${cartToken}/braintree/client_token?payment_account_id=${paymentAccountId}`,
    method: "GET",
    outputSchema: braintreeClientTokenResponseSchema,
  });
}

/**
 * Create a Braintree payment from a nonce (PayPal/Venmo).
 * POST /api/v202506/carts/:cartToken/braintree/create_payment
 */
export async function createBraintreePayment(
  cartToken: string,
  params: {
    payment_method_nonce: string;
    device_data?: string;
    payment_account_id: string;
    payer_email?: string;
    shipping_address?: {
      recipient_name?: string;
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country_code?: string;
    };
  },
) {
  return fetchApi({
    endpoint: `/${API_VER}/carts/${cartToken}/braintree/create_payment`,
    method: "POST",
    input: params,
    outputSchema: braintreePaymentResponseSchema,
  });
}

/**
 * Register Braintree payment method on the cart via the PayPal endpoint.
 * POST /carts/:cartToken/payment_methods/paypal
 * Uses sdk_type: "braintree" to differentiate from regular PayPal.
 */
export async function createBraintreePaymentMethod({
  cartToken,
  transactionId,
}: {
  cartToken: string;
  transactionId: string;
}) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/payment_methods/paypal`,
    method: "POST",
    input: {
      payment_method: {
        order_id: transactionId,
        token: transactionId,
        sdk_type: "braintree",
      },
    },
    outputSchema: braintreePaymentMethodResponseSchema,
  });
}
